/**
 * Dependency graph — reverse index from a symbol-table reference name to the
 * entities that reference it (PR2, plan §13).
 *
 * # Why this lives in `drawing-model` (shared CAD core)
 *
 * The cardinal constraint (plan §5.1 D5) is INDEX-OVER-EXISTING: no
 * `DrawingEntity` is allocated, no class-per-entity, the 685k-entity compact
 * store must not be defeated. This module owns the *pure* pieces of that index:
 *
 *   - `extractRefs(entity)` — read the symbol-table reference NAMES an entity
 *     carries (layer / style / block / linetype). Mirrors the field map the
 *     reference AUDIT walks (`referenceAudit.entityRefs.ts`) but FORWARD: it
 *     returns every ref the entity holds (resolved or dangling), because the
 *     dependency index must list ALL dependents of a name, not just the broken
 *     ones. Pure, never throws, never mutates.
 *
 *   - `DependencyIndex` — the reverse-index data structure itself. Keyed
 *     `Map<lowercasedRefName, Map<EntityRefKey, EntityRef>>` per ref-kind, where
 *     **`EntityRef = { container; id; kind }`** (NOT a bare id — a block-
 *     definition child and a model-space entity can legally share an id, and
 *     consumers like PURGE / RENAME must know WHICH container to rewrite;
 *     removed-mutations are id-only so delete-by-(kind,container,id) must be
 *     supported — plan §13 / Codex#2 P2-6). `kind` ("space" | "block")
 *     discriminates a space from a block definition whose name equals a space id
 *     so the two never collide on the key (PR2 Codex P2 #217). Pure data: no
 *     React, no zustand, no compact-store coupling.
 *
 *   - `buildDependencyIndex(document)` — bulk-build over a whole
 *     `DrawingDocument` (BOTH `spaces[].entities` AND
 *     `Object.entries(blocks)[].entities`).
 *
 * Putting the CLASS here (not only the helpers) lets BOTH the web incremental
 * index (`apps/web/.../dependencyIndex.ts`, which wires it into the
 * `DocumentMemoryHolder` + `commitMutation`) and `command-engine`
 * (`purgeBlocksMutation`, which has no access to the web holder) consume the
 * SAME implementation — no second copy of CAD semantics (priority #1).
 *
 * Source of truth: docs/superpowers/plans/2026-05-30-drawing-object-graph.md
 * §13 / §5.1 D5.
 */

import type {
  BlockChildEntity,
  DrawingDocument,
  DrawingEntity,
} from "./model";
// Reuse the audit's canonical sentinel set (DRY — priority #1) instead of a
// second hand-copy: SENT_LINETYPE = ByLayer/ByBlock/Continuous (not LTYPE-table
// names).
import { SENT_LINETYPE } from "./referenceAudit.sentinels";
// The ONE arrowhead-resolution shared with the export stamp + the audit
// (`builtinArrowNames.ts`): classifies a `dimblk*` NAME / `dimblk*Handle` HANDLE /
// `arrowheadBlock` override into a default / real-block / imported-handle / built-
// in-synthesized / dropped verdict, MIRRORING the export stamp's ordering — so the
// dependency index records the SAME live block dependents the exporter points its
// `1005` handles at (a canonical `_DOT` adopt R12, an imported-handle target R13),
// and drops the SAME sentinels (closed-filled / built-in / `"0"` R14).
import {
  resolveArrowheadRef,
  resolveMleaderArrowheadRef,
  type ArrowheadResolveContext,
} from "./builtinArrowNames";
// The SINGLE source of truth for the ARRAYRECT→MINSERT emit decision — the same
// predicate the exporter (dxfBridge.arrayMinsert) consumes. Reused here so the
// dependency index records `sourceBlockName` as a live BLOCK dependent EXACTLY
// when the export will emit a MINSERT of it, never for an associative / unresolved-
// extension-dictionary / 1×1 array whose `sourceBlockName` is inert (@codex SL-18
// R3 P2). Type-only edge to model keeps this import cycle-free.
import { arrayRectEmitsAsMInsert } from "./dxfBridge.arrayMinsert";

/** The four reference kinds the dependency index tracks (plan §13). */
export type DependencyRefKind = "layer" | "style" | "block" | "linetype";

/**
 * The KIND of container an `EntityRef.container` string names: a drawing SPACE
 * (model / paper layout — `container` is the space id) or a BLOCK DEFINITION
 * (`container` is the block name). Required on every `EntityRef` so the index
 * key carries a space-vs-block discriminator: a user block can legally be named
 * identically to a space id (e.g. a block literally named `model`, equal to the
 * model-space id `model`), and an entity id can legally repeat across containers
 * (plan §13 / Codex#2 P2-6). Without the kind, a block child and a space entity
 * sharing both `container` string AND `id` would collide on the SAME index key,
 * overwriting one dependent with the other (PR2 Codex P2 #217).
 */
export type DependencyContainerKind = "space" | "block";

/** All four ref-kinds — the default index scope. */
const ALL_REF_KINDS: ReadonlySet<DependencyRefKind> = new Set<DependencyRefKind>([
  "layer",
  "style",
  "block",
  "linetype",
]);

/**
 * Options for a `DependencyIndex`. Defaults reproduce the full incremental
 * index. A build-once / query / discard caller (e.g. command-engine PURGE,
 * which only ever asks `getDependents("block", …)`) narrows `kinds` and turns
 * `trackOwners` off so it does not allocate the other kind-maps or the forward
 * ownerBuckets it will never use — important on the 685k-entity drawings this
 * layer targets (priority #1: no fidelity loss — `extractRefs` still yields the
 * same block refs, so mleader/dim block references stay counted).
 */
export interface DependencyIndexOptions {
  /** Ref-kinds to index. Default: all four. */
  readonly kinds?: ReadonlySet<DependencyRefKind>;
  /**
   * Maintain the forward `ownerBuckets` map (needed for O(refs) incremental
   * `applyBatch` removal). Default true. A throwaway query index sets false —
   * `applyBatch` is then unsupported (its `removeRef` path no-ops without the
   * forward index).
   */
  readonly trackOwners?: boolean;
}

/**
 * A single dependent reference target. `container` is the SPACE id (for a
 * top-level entity) OR the BLOCK-DEFINITION name (for a block child), and `kind`
 * says WHICH of the two it is. `id` is the entity id within that container. Two
 * entities sharing an `id` across different containers are DISTINCT `EntityRef`s
 * — this is the whole reason the value is not a bare id (plan §13 / Codex#2
 * P2-6). `kind` further distinguishes a space whose id equals a block name (a
 * block literally named `model` vs the model-space id `model`) so the two never
 * collide on the index key (PR2 Codex P2 #217).
 */
export interface EntityRef {
  readonly container: string;
  readonly id: string;
  readonly kind: DependencyContainerKind;
}

/**
 * Stable string key for an `EntityRef` inside a Set/Map. `\0` (a TWO-character
 * `\` `0` escape — NOT a raw NUL byte, which would make git treat this file as
 * binary) cannot appear in a DXF space id, block name, or entity id, so it is a
 * safe field separator. The key is `kind\0container\0id`: the `kind` prefix
 * discriminates a SPACE from a BLOCK DEFINITION whose name equals a space id, so
 * a space entity and a block child that share BOTH the container string AND the
 * entity id still map to DISTINCT keys (PR2 Codex P2 #217 / plan §13 Codex#2 P2-6).
 */
export function entityRefKey(ref: EntityRef): string {
  return `${ref.kind}\0${ref.container}\0${ref.id}`;
}

/**
 * The reference NAMES one entity carries, grouped by kind. `styles` is the
 * UNION of every style reference the entity holds (Text/MText/Dimension
 * `styleName`; Multileader `styleName` + `textStyleName`; Attdef `styleName` +
 * `textStyleName`) so a single delete-preflight / purge query over `"style"`
 * sees every text/dim/mleader style the entity depends on. `blocks` is the
 * union of every block reference, split by classification (mirrors the audit,
 * referenceAudit.entityRefs.ts):
 *   - REAL BLOCK-table refs (no arrowhead bypass): INSERT `blockName`,
 *     Multileader CONTENT `blockName`, Dimension `anonymousBlockName`. A block
 *     literally named `_ClosedFilled`/`_Dot` IS a real dependent here.
 *   - ARROWHEAD-field refs. DIMENSION `styleOverrides.dimblk`/`dimblk1`/`dimblk2`
 *     (AUTHORED NAMEs) + `styleOverrides.dimblk*Handle` (IMPORTED `1005` soft-pointer
 *     HANDLEs) resolve via `resolveArrowheadRef`, which records a real declared block
 *     — INCLUDING a canonical `_Dot` a `DOT` ADOPTS (R12), and the block a handle
 *     targets (R13) — because the DIMENSION export canonicalizes/synthesizes arrows.
 *     Multileader `arrowheadBlock` + leader-line `arrowheadBlock` resolve via the
 *     DISTINCT `resolveMleaderArrowheadRef` (SL-11 round-6 @codex): the MLEADER
 *     arrowhead exports VERBATIM (no synthesis), so a dependent is recorded ONLY on a
 *     block the value LITERALLY names or the live BLOCK_RECORD an imported handle
 *     targets — NEVER a canonical `_Dot` twin. A `_ClosedFilled` / `"0"` / built-in-
 *     with-no-block / dropped custom name here is NOT a BLOCK ref, so it is excluded.
 *
 * Empty strings and the `ByLayer`/`ByBlock` linetype sentinels are EXCLUDED at
 * extract time — they are not real table references and would pollute the
 * reverse index (a `getDependents("linetype","ByLayer")` query is meaningless).
 */
export interface ExtractedRefs {
  layer?: string;
  styles: string[];
  blocks: string[];
  linetype?: string;
}

function pushName(out: string[], name: string | undefined): void {
  if (name === undefined || name === "") return;
  out.push(name);
}

/**
 * Record the BLOCK dependency a DIMENSION DSTYLE arrowhead-field NAME override
 * (`dimblk` / `dimblk1` / `dimblk2`) creates, via the shared
 * {@link resolveArrowheadRef}. (MLEADER `arrowheadBlock` uses the DISTINCT
 * {@link pushMleaderArrowheadRef} — no built-in synthesis.) Only a
 * `block-name` verdict is a live current-document dependent — a real declared
 * block, INCLUDING the canonical `_DOT` an unprefixed `DOT` adopts when that block
 * is declared (R12), which the exporter points its `1005` handle at, so PURGE must
 * keep it. `default` (closed-filled / `"0"` R14), `builtin-synthesized` (arrow
 * block created on export), and `unresolved` (dropped custom name) are NOT
 * purgeable dependencies.
 */
function pushArrowheadNameRef(
  out: string[],
  value: string | undefined,
  ctx: ArrowheadResolveContext,
): void {
  if (value === undefined || value === "") return;
  const res = resolveArrowheadRef(value, "name", ctx);
  if (res.kind === "block-name") out.push(res.name);
}

/**
 * Record the BLOCK dependency an IMPORTED arrowhead-field HANDLE override
 * (`dimblk*Handle`, a DSTYLE `1005` soft-pointer) creates (R13). A `block-handle`
 * verdict resolves the handle → its live BLOCK_RECORD's NAME; the dependency is
 * recorded on THAT block so PURGE keeps an arrow block referenced ONLY by handle
 * (the exporter passes the imported `1005` through verbatim). The null handle
 * `"0"` (default) and a dangling handle (unresolved) create no dependency.
 *
 * SL-11 round-7 (@codex F1) — the paired `dimblk*` NAME override WINS on export:
 * `buildDimensionStyleOverridesXData` emits the `dimblk*Handle` twin as `1005
 * <handle>` ONLY when the name override is `undefined` (its condition is
 * `def.blockRef && value === undefined && importedHandle !== undefined`); when the
 * name IS defined it emits the `1000 <name>` placeholder and the handle twin is
 * NEVER written — a DEAD handle. A dead handle must record NO dependency (else
 * PURGE keeps a block the export does not reference). `nameOverride` is the paired
 * `dimblk*` value; when it is defined the handle is skipped, mirroring the EXACT
 * round-6 `referenceAudit.entityRefs.ts` precedence so audit + dependency agree.
 */
function pushArrowheadHandleRef(
  out: string[],
  handle: string | undefined,
  nameOverride: string | undefined,
  ctx: ArrowheadResolveContext,
): void {
  if (handle === undefined || handle === "") return;
  if (nameOverride !== undefined) return; // name override wins → handle is dead
  const res = resolveArrowheadRef(handle, "handle", ctx);
  if (res.kind === "block-handle") out.push(res.name);
}

/**
 * Record the BLOCK dependency an MLEADER / leader-line `arrowheadBlock` creates, via
 * the MLEADER-specific {@link resolveMleaderArrowheadRef} (SL-11 round-6 @codex).
 *
 * The MLEADER arrowhead is exported VERBATIM (body 342, or a NAME→handle drop for
 * MLEADERSTYLE) with NO built-in synthesis — UNLIKE the DIMENSION DSTYLE path. So a
 * dependency is recorded ONLY on a REAL block the value LITERALLY names, or on the
 * live BLOCK_RECORD an imported handle targets — NEVER on a synthesized built-in
 * twin (an `arrowheadBlock:"DOT"` does NOT record a dep on a declared `_DOT`; the
 * MLEADER copies `DOT` verbatim and does not adopt `_DOT`, so routing it through the
 * DIMENSION resolver's R12 canonicalization would keep `_DOT` in-use for a PURGE
 * that should drop it). `default` / `unresolved` create no dependency.
 */
function pushMleaderArrowheadRef(
  out: string[],
  value: string | undefined,
  ctx: ArrowheadResolveContext,
): void {
  if (value === undefined || value === "") return;
  const res = resolveMleaderArrowheadRef(value, ctx);
  if (res.kind === "block-name" || res.kind === "block-handle") out.push(res.name);
}

/**
 * Read every symbol-table reference NAME an entity (or block child) carries.
 *
 * Mirrors the per-type field map the reference audit walks
 * (`referenceAudit.entityRefs.ts`, §4) but forward and resolved-agnostic: it
 * lists ALL references so the dependency index can answer "who depends on name
 * X" exhaustively. Pure — reads only the passed object, never throws.
 *
 * NOTE the `layer`/`linetype` base fields keep their ByLayer/ByBlock sentinels
 * excluded the same way as `blocks`: an empty/sentinel linetype is filtered
 * (handled in the caller via `LINETYPE_SENTINELS`); `layer` has no sentinel
 * (the layer name is always a real table reference) but an empty string is
 * still excluded defensively.
 *
 * `definedBlockNames` (R9/R12) is the set of LOWERCASED block NAMES the document
 * declares, and `blockRecordHandlesToName` (R13) maps a LOWERCASED BLOCK_RECORD
 * handle → its block NAME. Both feed the shared {@link resolveArrowheadRef}, so an
 * arrowhead-field override is recorded as a dependent EXACTLY when the export
 * stamp points a live `1005` handle at a current-document block:
 *   - a `dimblk*` / `arrowheadBlock` NAME that matches a declared block — INCLUDING
 *     the canonical `_Dot` an unprefixed `DOT` adopts when `_Dot` is declared (R12);
 *   - a `dimblk*Handle` IMPORTED soft-pointer that targets a live BLOCK_RECORD —
 *     resolved handle → block NAME so PURGE keeps a block referenced ONLY by handle
 *     (R13).
 * Built-in arrows with no declared block (synthesized on export), the closed-filled
 * / `"0"` default (R14), and dropped custom names create NO dependency. Omit either
 * map at a site with no such context → those resolutions simply do not fire.
 */
export function extractRefs(
  entity: DrawingEntity | BlockChildEntity,
  definedBlockNames?: ReadonlySet<string>,
  blockRecordHandlesToName?: ReadonlyMap<string, string>,
): ExtractedRefs {
  const styles: string[] = [];
  const blocks: string[] = [];
  const arrowheadCtx: ArrowheadResolveContext = {
    definedBlockNames,
    blockRecordHandlesToName,
  };

  // ── Base fields (every entity) ──────────────────────────────────────────
  const layer =
    entity.layer && entity.layer !== "" ? entity.layer : undefined;
  let linetype: string | undefined;
  if (
    entity.linetype &&
    entity.linetype !== "" &&
    !SENT_LINETYPE.has(entity.linetype.toLowerCase())
  ) {
    linetype = entity.linetype;
  }

  // ── Per-kind references (mirror §4 / referenceAudit.entityRefs.ts) ───────
  switch (entity.type) {
    case "Text":
    case "MText":
      pushName(styles, entity.styleName);
      break;
    case "Dimension":
      pushName(styles, entity.styleName);
      // anonymousBlockName is a REAL BLOCK-table ref (the `*D nn` dim geometry
      // block) — the audit resolves it with SENT_NONE (no arrowhead bypass,
      // referenceAudit.entityRefs.ts §Dimension), so it must NOT pass through the
      // arrowhead resolver (anon names never collide with the `_arrow` sentinels,
      // but mirror the audit's exact classification) — it is a plain `pushName`.
      pushName(blocks, entity.anonymousBlockName);
      // dimblk / dimblk1 / dimblk2 are AUTHORED arrowhead NAMEs; dimblk*Handle are
      // IMPORTED `1005` soft-pointer HANDLEs. Both go through the shared resolver so
      // the dependency matches the exporter's `1005` target: a real declared block /
      // canonical `_Dot` adopt (R12) for a NAME, or the live BLOCK_RECORD a handle
      // points at (R13). Closed-filled / `"0"` / built-in-synthesized / dangling →
      // no dependency. SL-11 round-7 (@codex F1): a `dimblk*Handle` is DEAD (never
      // emitted) when its paired `dimblk*` NAME is defined — the name wins on export
      // — so the paired name is threaded to `pushArrowheadHandleRef` to skip the dead
      // handle, mirroring the round-6 audit precedence (audit + dependency agree).
      pushArrowheadNameRef(blocks, entity.styleOverrides?.dimblk, arrowheadCtx);
      pushArrowheadNameRef(blocks, entity.styleOverrides?.dimblk1, arrowheadCtx);
      pushArrowheadNameRef(blocks, entity.styleOverrides?.dimblk2, arrowheadCtx);
      pushArrowheadHandleRef(
        blocks,
        entity.styleOverrides?.dimblkHandle,
        entity.styleOverrides?.dimblk,
        arrowheadCtx,
      );
      pushArrowheadHandleRef(
        blocks,
        entity.styleOverrides?.dimblk1Handle,
        entity.styleOverrides?.dimblk1,
        arrowheadCtx,
      );
      pushArrowheadHandleRef(
        blocks,
        entity.styleOverrides?.dimblk2Handle,
        entity.styleOverrides?.dimblk2,
        arrowheadCtx,
      );
      break;
    case "Multileader":
      pushName(styles, entity.styleName);
      pushName(styles, entity.textStyleName);
      // blockName is the multileader's CONTENT block — a REAL BLOCK-table ref
      // (audit resolves with SENT_NONE, referenceAudit.entityRefs.ts §Multileader
      // blockName). A drawing may legitimately contain a block literally named
      // `_ClosedFilled`/`_Dot` (arrowhead blocks materialise into the BLOCKS
      // table when used), so it must NOT be dropped by the arrowhead filter —
      // otherwise PURGE would mark the referenced block purgeable and delete it,
      // dangling this INSERT/multileader (PR2 Codex P2 #217).
      pushName(blocks, entity.blockName);
      // arrowheadBlock is the MLEADER arrowhead field — exported VERBATIM (body 342)
      // with NO built-in synthesis (UNLIKE the DIMENSION DSTYLE arrowheads). The
      // MLEADER-specific resolver records a dependent ONLY on a block the value
      // LITERALLY names or a live BLOCK_RECORD an imported handle targets — never a
      // canonical `_<NAME>` built-in adopt (SL-11 round-6 @codex: routing this
      // through the DIMENSION resolver recorded a false `_DOT` dependency). The
      // closed-filled `_ClosedFilled` / `"0"` default + built-in-with-no-block +
      // dropped custom name create no dependency.
      pushMleaderArrowheadRef(blocks, entity.arrowheadBlock, arrowheadCtx);
      for (const root of entity.leaderRoots ?? []) {
        for (const node of root.nodes ?? []) {
          for (const line of node.lines ?? []) {
            pushMleaderArrowheadRef(blocks, line.arrowheadBlock, arrowheadCtx);
          }
        }
      }
      break;
    case "Block":
      // An INSERT's blockName is ALWAYS a real BLOCK-table reference (the audit
      // resolves it as a REQUIRED ref with SENT_NONE — referenceAudit.entityRefs
      // .ts §Block). It must NOT pass through the arrowhead filter: a block can
      // legally be named `_ClosedFilled`/`_Dot` (arrowhead blocks materialise
      // into the BLOCKS table), and filtering it would let PURGE delete a block
      // a live INSERT still references (PR2 Codex P2 #217).
      pushName(blocks, entity.blockName);
      for (const attr of entity.attributes ?? []) {
        pushName(styles, attr.overrideStyle);
      }
      break;
    case "Attdef":
      pushName(styles, entity.styleName);
      break;
    case "Table":
      // ACAD_TABLE group 2 — the ANONYMOUS `*T<N>` block that holds the
      // table's rendered graphics (the MTEXT of every cell and the LINE
      // grid). It is a REAL BLOCK-table reference, exactly like an INSERT's
      // blockName: AutoCAD serializes a table as a block reference to that
      // block plus the AcDbTable content model, the exporter re-emits the
      // group-2 name verbatim, and the renderer draws that block's content.
      //
      // Without this arm the block had NO dependent, so PURGE analysis
      // (`purgeBlocksMutation` → `getDependents("block", …)`) reported a
      // graphics block referenced only by a table as `ready` and
      // `applyPurgeBlocks` DELETED it — leaving the table pointing at a
      // block that no longer exists and rendering nothing. `*T<N>` is NOT
      // added to `isAnonymousBlockName`'s reserved set on purpose: that
      // would make an ORPHANED graphics block (its table erased) permanently
      // unpurgeable, trading a deletion bug for a leak. The dependency is
      // what decides it, and the dependency is exact.
      //
      // Deliberately NOT routed through the arrowhead resolver (same reason
      // as INSERT / Multileader `blockName`): a table's graphics block is a
      // required ref and must never be filtered by an arrowhead sentinel.
      pushName(blocks, entity.blockName);
      break;
    case "Rectangular Associative Array":
      // Batch-8 S-ARRAYASSOC-MODEL. The associative array's `sourceEntityIds[]`
      // are ENTITY-GRAPH references (entity id → entity id), NOT symbol-table
      // NAME references — this extractor returns only symbol-table refs
      // (layer / style / block / linetype names) that a PURGE / rename
      // preflight rewrites. Folding entity ids into `blocks`/`styles` here
      // would misclassify them as table names and let a name-keyed preflight
      // corrupt the index. The source-entity dependency is instead tracked by
      // the array-membership mechanism (`arrayGroupId` / `arraySourceOfGroup`
      // base fields) + the typed `sourceEntityIds[]` the ARRAYEDIT flow reads.
      //
      // SL-18 — `sourceBlockName` (DXF group 2) is a real BLOCK-table reference
      // ONLY when the array actually re-emits as a genuine MINSERT (`0 INSERT` +
      // AcDbMInsertBlock) of that block. GATED on the exporter's own
      // `arrayRectEmitsAsMInsert` predicate so it fires for a NON-associative
      // real grid but NOT for an ASSOCIATIVE array, an array with an unresolved
      // extension dictionary, or a 1×1 array — for those the exporter keeps the
      // `0 ARRAYRECT` block reference and never references `sourceBlockName`, so
      // indexing it would make PURGE count an otherwise-unused block as in-use
      // and let a RENAME preflight rewrite a name that is never emitted (@codex
      // SL-18 R3 P2). `sourceBlockDefined = true`: the graph records the
      // dependency for whatever block the MINSERT WOULD point at — a name absent
      // from the BLOCKS table is a harmless PURGE/rename no-op, while the
      // emit-shape gate (non-assoc, no assoc graph, real grid, real source name)
      // is what actually determines the dependency. `pushName` drops
      // undefined/empty, so a reducer-built array with no source block records no
      // false dependency.
      if (arrayRectEmitsAsMInsert(entity, true)) {
        pushName(blocks, entity.sourceBlockName);
      }
      break;
    default:
      // Line / Point / Circle / Arc / Ellipse / Spline / LWPOLYLINE / Hatch /
      // Viewport / Revision Cloud / Wipeout / Polyline / OLE Frame — only
      // carry the base
      // layer/linetype refs (already captured above). The Viewport
      // `frozenLayers[]` are layer-VISIBILITY overrides, not the ownership ref
      // a layer-delete preflight rewrites, so they are excluded (mirrors the
      // metadata layer aggregate, which keys on `entity.layer`).
      break;
  }

  return { layer, styles, blocks, linetype };
}

/** One container of entities to bulk-load (a space OR a block definition). */
export interface DependencyContainer {
  /** Space id OR block-definition name. */
  readonly container: string;
  /** Whether `container` names a SPACE or a BLOCK DEFINITION (key discriminator). */
  readonly kind: DependencyContainerKind;
  /**
   * The container's entities, consumed by `bulkLoad` with a SINGLE `for…of`
   * pass. Declared `Iterable` (not `ReadonlyArray`) so a STREAMED space can
   * hand in a GENERATOR that materialises one `DrawingEntity` at a time from
   * the compact store instead of a full per-space `DrawingEntity[]` snapshot
   * (~150–300 MB on 685k-entity drawings) — `bulkLoad` only `extractRefs` each
   * entity once and retains a tiny `EntityRef{container,id,kind}`, never the
   * entity itself, so iterating a generator yields the IDENTICAL ref set with
   * O(1) peak entity memory (INDEX-OVER-EXISTING, plan §5.1 D5). An array IS an
   * `Iterable`, so inline / block-definition callers passing the document's
   * own arrays are unaffected.
   */
  readonly entities: Iterable<DrawingEntity | BlockChildEntity>;
}

/**
 * The incremental-update batch shape — mirrors the metadata index
 * `applyBatch({ upserts, deletes })` contract (`commitMutation.ts`). `added`
 * and `updated` carry the full entity (so refs can be extracted); `removed` is
 * id-only (a removed-mutation only declares `{ spaceId, entityId }`), so the
 * index deletes by `(container, id)` without needing the gone entity's refs.
 */
export interface DependencyBatch {
  readonly added: ReadonlyArray<{
    container: string;
    kind: DependencyContainerKind;
    entity: DrawingEntity | BlockChildEntity;
  }>;
  readonly removed: ReadonlyArray<{
    container: string;
    kind: DependencyContainerKind;
    id: string;
  }>;
  readonly updated: ReadonlyArray<{
    container: string;
    kind: DependencyContainerKind;
    prev: DrawingEntity | BlockChildEntity;
    next: DrawingEntity | BlockChildEntity;
  }>;
}

/**
 * Reverse dependency index. For each ref-kind, a case-insensitive
 * `Map<lowercasedName, Map<EntityRefKey, EntityRef>>`. The inner map keys on a
 * stable `container\0id` string (so re-adding the same ref is idempotent) and
 * stores the resolved `EntityRef` so `getDependents` can return them directly.
 *
 * Pure data structure — no compact-store / holder coupling; the web layer wraps
 * it (`apps/web/.../dependencyIndex.ts`) to wire `bulkLoad` on import +
 * `applyBatch` on `commitMutation`, and `command-engine` builds a throwaway one
 * over a `DrawingDocument` for PURGE.
 */
export class DependencyIndex {
  private readonly byLayer = new Map<string, Map<string, EntityRef>>();
  private readonly byStyle = new Map<string, Map<string, EntityRef>>();
  private readonly byBlock = new Map<string, Map<string, EntityRef>>();
  private readonly byLinetype = new Map<string, Map<string, EntityRef>>();
  /**
   * Forward index: `entityRefKey → the (kindMap, lowercasedName) buckets that
   * entity ref was inserted into`. Lets `removeRef` delete a (container,id) in
   * O(refs-it-holds) instead of scanning every name-bucket of every kind-map —
   * critical so a large delete batch does not become O(deleted × table
   * cardinality) (would defeat the 685k-store perf intent, D5).
   */
  private readonly ownerBuckets = new Map<
    string,
    Array<{ map: Map<string, Map<string, EntityRef>>; name: string }>
  >();

  /**
   * LOWERCASED set of block NAMES the loaded document declares — the R9
   * real-block-wins context handed to `extractRefs` so a dimblk/arrowheadBlock
   * override naming a DECLARED block (e.g. a real block called `_Dot`) is
   * recorded as a dependent instead of dropped as a built-in-arrow sentinel.
   * Rebuilt from the block-def containers on every `bulkLoad`; incrementally
   * grown (add-only) by `applyBatch` when a batch defines new block containers.
   */
  private readonly definedBlockNames = new Set<string>();

  /**
   * LOWERCASED BLOCK_RECORD handle → its block NAME — the R13 imported-handle
   * context handed to `extractRefs` so a `dimblk*Handle` soft-pointer that targets
   * a live BLOCK_RECORD records a dependent on THAT block (PURGE keeps an arrow
   * block referenced ONLY by handle). Set on `bulkLoad` from the document's
   * `blockRecords`; NOT grown by `applyBatch` (BLOCK_RECORD handles are not carried
   * by an entity-mutation batch — same class as the "block defined after the dim
   * was indexed is not retroactively re-extracted" limitation below; rebuilt on the
   * next `bulkLoad`). The primary R13 consumer (command-engine PURGE) builds a fresh
   * index each run, so it always sees the current handle map.
   */
  private readonly blockRecordHandlesToName = new Map<string, string>();

  private readonly kinds: ReadonlySet<DependencyRefKind>;
  private readonly trackOwners: boolean;

  constructor(options?: DependencyIndexOptions) {
    this.kinds = options?.kinds ?? ALL_REF_KINDS;
    this.trackOwners = options?.trackOwners ?? true;
  }

  private kindMap(kind: DependencyRefKind): Map<string, Map<string, EntityRef>> {
    switch (kind) {
      case "layer":
        return this.byLayer;
      case "style":
        return this.byStyle;
      case "block":
        return this.byBlock;
      case "linetype":
        return this.byLinetype;
    }
  }

  /** Drop every entry — used before a full rebuild / on store reset. */
  clear(): void {
    this.byLayer.clear();
    this.byStyle.clear();
    this.byBlock.clear();
    this.byLinetype.clear();
    this.ownerBuckets.clear();
    this.definedBlockNames.clear();
    this.blockRecordHandlesToName.clear();
  }

  /**
   * Rebuild from scratch over a list of containers (each a space or a block
   * definition). Mirrors the metadata index `bulkLoad`. Each container's
   * `entities` is iterated EXACTLY ONCE (`extractRefs` → store a tiny
   * `EntityRef`, never the entity), so a container may stream its entities via
   * a generator (a STREAMED space materialising from the compact store one
   * entity at a time) instead of a full array — same ref set, O(1) peak entity
   * memory (plan §5.1 D5). A one-shot generator is fine: it is consumed exactly
   * once here.
   *
   * `blockRecordHandlesToName` (R13) is the LOWERCASED BLOCK_RECORD handle → block
   * NAME map (from the document's `blockRecords`) so a dimension's `dimblk*Handle`
   * imported soft-pointer resolves to the block it targets. Optional: omit it (the
   * pre-R13 shape) and only NAME-based arrowhead deps are recorded — a `dimblk*Handle`
   * simply does not resolve. Both maps are collected BEFORE `extractRefs` runs.
   */
  bulkLoad(
    containers: Iterable<DependencyContainer>,
    blockRecordHandlesToName?: ReadonlyMap<string, string>,
  ): void {
    this.clear();
    if (blockRecordHandlesToName) {
      for (const [handle, name] of blockRecordHandlesToName) {
        this.blockRecordHandlesToName.set(handle.toLowerCase(), name);
      }
    }
    // Materialise the container LIST (not the entities — each container's
    // `entities` stays a lazy iterable, consumed exactly once in the ref pass
    // below) so the R9 defined-block-name set can be collected BEFORE
    // `extractRefs` runs. Every block-def container's `container` field IS a
    // declared block name, so no separate document handle is needed. Reading
    // only `c.kind`/`c.container` here never touches (or exhausts) a streamed
    // space's entity generator.
    const list: DependencyContainer[] = Array.isArray(containers)
      ? containers
      : [...containers];
    for (const c of list) {
      if (c.kind === "block") this.definedBlockNames.add(c.container.toLowerCase());
    }
    for (const c of list) {
      for (const entity of c.entities) {
        this.addEntity(c.container, c.kind, entity);
      }
    }
  }

  /**
   * Incremental update from a `commitMutation` batch. `added` inserts refs,
   * `removed` deletes by `(container, id)` (id-only — the gone entity's refs
   * are not needed because every ref-kind map is scanned for that key), and
   * `updated` diffs prev→next (removes prev's refs, adds next's). Mirrors the
   * metadata index `applyBatch` so the two stay in lockstep on every commit.
   */
  applyBatch(batch: DependencyBatch): void {
    // R9 — a block newly DEFINED in this batch (its children arrive as `block`-
    // kind adds/updates) must be recognised as a real block FIRST, so an entity
    // added in the SAME batch whose dimblk names it is recorded as a dependent
    // rather than dropped as a built-in-arrow sentinel. Add-only: a removed
    // block name is left in the set harmlessly — once the block is gone nobody
    // queries getDependents for it; the full name set is rebuilt on next
    // bulkLoad. (A block defined AFTER the referencing dim was already indexed
    // is not retroactively re-extracted — same class as the streamed-dimension
    // limitation documented in apps/web/.../dependencyIndex.ts.)
    for (const a of batch.added) {
      if (a.kind === "block") this.definedBlockNames.add(a.container.toLowerCase());
    }
    for (const u of batch.updated) {
      if (u.kind === "block") this.definedBlockNames.add(u.container.toLowerCase());
    }
    for (const r of batch.removed) {
      this.removeRef(r.container, r.kind, r.id);
    }
    for (const u of batch.updated) {
      // prev and next may carry the SAME id (the common case) or a renamed id
      // (type-conversion mutations). Remove the prev's refs by its id, then add
      // the next's. `removeRef` scans all kind-maps for the (kind,container,id)
      // key, so a stale prev id is cleaned even when next renames it.
      this.removeRef(u.container, u.kind, u.prev.id);
      this.addEntity(u.container, u.kind, u.next);
    }
    for (const a of batch.added) {
      this.addEntity(a.container, a.kind, a.entity);
    }
  }

  /**
   * Dependents of `name` for `refKind`, case-insensitive. Returns a fresh array
   * (safe for the caller to sort / mutate). Empty array for an unknown name.
   */
  getDependents(refKind: DependencyRefKind, name: string): readonly EntityRef[] {
    const inner = this.kindMap(refKind).get(name.toLowerCase());
    if (inner === undefined) return EMPTY_REFS;
    return Array.from(inner.values());
  }

  /** Number of distinct dependent refs of `name` for `refKind`. */
  getDependentCount(refKind: DependencyRefKind, name: string): number {
    return this.kindMap(refKind).get(name.toLowerCase())?.size ?? 0;
  }

  /** Diagnostic — number of distinct names tracked for a ref-kind. */
  nameCount(refKind: DependencyRefKind): number {
    return this.kindMap(refKind).size;
  }

  private addEntity(
    container: string,
    kind: DependencyContainerKind,
    entity: DrawingEntity | BlockChildEntity,
  ): void {
    const refs = extractRefs(
      entity,
      this.definedBlockNames,
      this.blockRecordHandlesToName,
    );
    const ref: EntityRef = { container, id: entity.id, kind };
    const key = entityRefKey(ref);
    if (this.kinds.has("layer") && refs.layer !== undefined) {
      this.insert(this.byLayer, refs.layer, key, ref);
    }
    if (this.kinds.has("linetype") && refs.linetype !== undefined) {
      this.insert(this.byLinetype, refs.linetype, key, ref);
    }
    if (this.kinds.has("style")) {
      for (const s of refs.styles) this.insert(this.byStyle, s, key, ref);
    }
    if (this.kinds.has("block")) {
      for (const b of refs.blocks) this.insert(this.byBlock, b, key, ref);
    }
  }

  private insert(
    map: Map<string, Map<string, EntityRef>>,
    name: string,
    key: string,
    ref: EntityRef,
  ): void {
    const lc = name.toLowerCase();
    let inner = map.get(lc);
    if (inner === undefined) {
      inner = new Map();
      map.set(lc, inner);
    }
    inner.set(key, ref);
    // Record the forward edge so removeRef can target this exact bucket. Skip
    // duplicate (map,name) entries for the same key (an entity referencing the
    // same name twice in one kind, e.g. dimblk1 == dimblk2) so removal is exact.
    if (!this.trackOwners) return;
    let buckets = this.ownerBuckets.get(key);
    if (buckets === undefined) {
      buckets = [];
      this.ownerBuckets.set(key, buckets);
    }
    if (!buckets.some((b) => b.map === map && b.name === lc)) {
      buckets.push({ map, name: lc });
    }
  }

  /**
   * Delete every reference whose `(kind, container, id)` matches, across ALL
   * ref-kinds. id-only deletion (no entity payload needed): the forward
   * `ownerBuckets` map yields exactly the (kindMap, name) buckets this entity
   * ref was inserted into, so removal is O(refs-the-entity-held), NOT a scan of
   * every name-bucket. A bucket that becomes empty is dropped so `nameCount`
   * reflects only live names. The `kind` is part of the key so removing a SPACE
   * ref never touches a BLOCK child that shares the container string + id
   * (PR2 Codex P2 #217).
   */
  private removeRef(
    container: string,
    kind: DependencyContainerKind,
    id: string,
  ): void {
    const key = entityRefKey({ container, id, kind });
    const buckets = this.ownerBuckets.get(key);
    if (buckets === undefined) return;
    for (const { map, name } of buckets) {
      const inner = map.get(name);
      if (inner !== undefined && inner.delete(key) && inner.size === 0) {
        map.delete(name);
      }
    }
    this.ownerBuckets.delete(key);
  }
}

const EMPTY_REFS: readonly EntityRef[] = Object.freeze([]);

/**
 * Build a fresh `DependencyIndex` over a whole `DrawingDocument`. Walks BOTH
 * `spaces[].entities` (container = space id) AND `Object.entries(blocks)`
 * (container = block-definition name; `block.entities` are full
 * `BlockChildEntity`). Used by `command-engine` (PURGE) and as the
 * non-streaming web build path. Pure — never mutates the document.
 *
 * For a STREAMING-imported document the `spaces[].entities` are discarded after
 * streaming, so the web layer rematerialises them from the compact store and
 * uses `DependencyIndex.bulkLoad(containers, handles)` directly instead of this
 * helper — see `apps/web/.../dependencyIndex.ts`.
 */
export function buildDependencyIndex(
  document: DrawingDocument,
  options?: DependencyIndexOptions,
): DependencyIndex {
  const index = new DependencyIndex(options);
  index.bulkLoad(
    dependencyContainersFromDocument(document),
    blockRecordHandlesToNameFromDocument(document),
  );
  return index;
}

/**
 * The LOWERCASED BLOCK_RECORD handle → block NAME map for the R13 imported-handle
 * arrowhead resolution: a dimension's `dimblk*Handle` soft-pointer (a `1005`
 * BLOCK_RECORD handle) resolves to the block it targets so PURGE keeps an arrow
 * block referenced ONLY by handle. Built from `document.blockRecords` (a name-keyed
 * Record, each entry carrying an optional group-5 `handle`); records with no handle
 * are skipped (nothing to resolve them by). Exposed so the web layer feeds the SAME
 * map to a pre-existing index (`bulkLoad(containers, handles)`).
 *
 * SL-11 round-6 (@codex F2) — the value is the MAP KEY, NOT the payload `rec.name`.
 * `exportBlockRecords` (dxfBridge.blockRecords.ts) serializes each BLOCK_RECORD's
 * name from the `document.blockRecords` KEY, and PURGE queries `getDependents`
 * against the `document.blocks` KEY (both name-keyed by the same convention). When a
 * record's KEY and its payload `rec.name` diverge (a rename that updated the payload
 * before re-keying, or a programmatically-built doc), indexing the handle dep under
 * the stale payload name would record it under a name no exported BLOCK_RECORD /
 * `document.blocks` entry carries → PURGE marks the real arrow block purgeable while
 * the exported DSTYLE `1005` still points at it. Keying by the KEY matches export.
 *
 * NULL-SAFE: `blockRecords` is a schema-defaulted `{}` on a parsed document, but a
 * hand-built / partial fixture doc (e.g. the `cmd-purge-blocks-mixed` PURGE
 * preflight) may omit it entirely — treat an absent map as "no records", yielding
 * an EMPTY handle map (zero handle-dependencies), never a throw.
 */
export function blockRecordHandlesToNameFromDocument(
  document: DrawingDocument,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const [name, rec] of Object.entries(document.blockRecords ?? {})) {
    if (rec?.handle) map.set(rec.handle.toLowerCase(), name);
  }
  return map;
}

/**
 * The container list `buildDependencyIndex` loads: every space (keyed by space
 * id) plus every block definition (keyed by block name). Exposed so callers
 * that already hold the document can feed the SAME container shape to a
 * pre-existing index instance (e.g. the web layer merges block-def containers
 * with compact-store-rematerialised space containers for streamed docs).
 */
export function dependencyContainersFromDocument(
  document: DrawingDocument,
): DependencyContainer[] {
  const containers: DependencyContainer[] = [];
  for (const space of document.spaces) {
    containers.push({
      container: space.id,
      kind: "space",
      entities: space.entities,
    });
  }
  for (const [name, def] of Object.entries(document.blocks)) {
    containers.push({ container: name, kind: "block", entities: def.entities });
  }
  return containers;
}

/**
 * The BLOCK-DEFINITION containers only (no spaces). The web streaming path uses
 * this to combine block-def edges (from the document, which always retains
 * `blocks`) with space edges rematerialised from the compact store.
 */
export function blockDependencyContainersFromDocument(
  document: DrawingDocument,
): DependencyContainer[] {
  const containers: DependencyContainer[] = [];
  for (const [name, def] of Object.entries(document.blocks)) {
    containers.push({ container: name, kind: "block", entities: def.entities });
  }
  return containers;
}
